import { Button, Container, Label } from '@playcanvas/pcui';

type WorkspaceMode = 'setup' | 'animate' | 'review';
type WorkspacePanel = 'scene' | 'view' | 'color' | 'timeline' | 'case' | 'evidence' | 'annotation';

type WorkspaceSwitcherOptions = {
    onModeChange: (mode: WorkspaceMode) => void;
    onPanelSelect: (panel: WorkspacePanel) => void;
};

const MODE_PANELS: Record<WorkspaceMode, Array<{ panel: WorkspacePanel; label: string }>> = {
    setup: [
        { panel: 'scene', label: 'Scene' },
        { panel: 'view', label: 'View' },
        { panel: 'color', label: 'Color' }
    ],
    animate: [
        { panel: 'scene', label: 'Scene' },
        { panel: 'view', label: 'View' },
        { panel: 'timeline', label: 'Timeline' }
    ],
    review: [
        { panel: 'case', label: 'Case' },
        { panel: 'evidence', label: 'Evidence' },
        { panel: 'annotation', label: 'Notes' }
    ]
};

class WorkspaceSwitcher extends Container {
    private modeButtons = new Map<WorkspaceMode, Button>();
    private panelRow: Container;
    private summary: Label;
    private onModeChange: (mode: WorkspaceMode) => void;
    private onPanelSelect: (panel: WorkspacePanel) => void;

    private bindButtonAction(button: Button, action: () => void) {
        button.dom.addEventListener('pointerdown', (event) => {
            event.preventDefault();
            event.stopPropagation();
            action();
        });

        button.dom.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                action();
            }
        });
    }

    constructor({ onModeChange, onPanelSelect }: WorkspaceSwitcherOptions, args = {}) {
        super({
            ...args,
            id: 'workspace-switcher'
        });

        this.onModeChange = onModeChange;
        this.onPanelSelect = onPanelSelect;

        const badge = new Label({
            text: 'Experimental UI',
            class: 'workspace-switcher-badge'
        });

        this.summary = new Label({
            text: 'Setup workspace active',
            class: 'workspace-switcher-summary'
        });

        const modeRow = new Container({
            class: 'workspace-switcher-mode-row'
        });

        (['setup', 'animate', 'review'] as WorkspaceMode[]).forEach((mode) => {
            const button = new Button({
                text: mode[0].toUpperCase() + mode.slice(1),
                class: 'workspace-switcher-chip'
            });

            this.bindButtonAction(button, () => {
                this.onModeChange(mode);
            });

            this.modeButtons.set(mode, button);
            modeRow.append(button);
        });

        this.panelRow = new Container({
            class: 'workspace-switcher-panel-row'
        });

        this.append(badge);
        this.append(this.summary);
        this.append(modeRow);
        this.append(this.panelRow);

        this.refresh('setup', 'scene');
    }

    refresh(mode: WorkspaceMode, panel: WorkspacePanel) {
        this.modeButtons.forEach((button, key) => {
            button.class[key === mode ? 'add' : 'remove']('active');
        });

        const activePanelLabel = MODE_PANELS[mode].find(entry => entry.panel === panel)?.label ?? 'Scene';
        this.summary.text = `${mode[0].toUpperCase() + mode.slice(1)} · ${activePanelLabel}`;

        this.panelRow.clear();

        MODE_PANELS[mode].forEach(({ panel: panelKey, label }) => {
            const button = new Button({
                text: label,
                class: 'workspace-switcher-subchip'
            });

            this.bindButtonAction(button, () => {
                this.onPanelSelect(panelKey);
            });

            button.class[panelKey === panel ? 'add' : 'remove']('active');
            this.panelRow.append(button);
        });
    }
}

export { WorkspaceSwitcher };
export type { WorkspaceMode, WorkspacePanel };